import React from 'react';
import { observer } from 'mobx-react-lite';

import { FormControl, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

import {
  DocumentAccessLevel,
  DocumentPermissionKind,
  DocumentPermissionResponse,
} from 'utils/api/modules/entities/entities.types';
import { UuidableName } from 'utils/api/types';

import {
  ShareDialogSection,
  ShareDialogSectionTitle,
  PeopleWithAccessList as StyledList,
  AccessRow,
  AccessRowInfo,
  AccessRowNameColumn,
  AccessRowName,
  AccessRowEmail,
  AccessRowActions,
  OwnerLabel,
  EmptyPeopleMessage,
  GroupRowIcon,
} from './styles';
import Avatar from 'components/Avatar/Avatar';

const memberLabel = (count: number): string => `${count} ${count === 1 ? 'member' : 'members'}`;

interface PeopleWithAccessListProps {
  owner: UuidableName | null;
  permissions: DocumentPermissionResponse[];
  isOwnerOrAdmin: boolean;
  onUpdate: (permissionId: string, accessLevel: DocumentAccessLevel) => void;
  onRemove: (permissionId: string) => void;
  onTransferOwnership: () => void;
}

const PeopleWithAccessList: React.FC<PeopleWithAccessListProps> = observer(
  ({ owner, permissions, isOwnerOrAdmin, onUpdate, onRemove, onTransferOwnership }) => (
    <ShareDialogSection>
      <ShareDialogSectionTitle>People with access</ShareDialogSectionTitle>
      <StyledList>
        {owner && (
          <AccessRow>
            <AccessRowInfo>
              <Avatar name={owner.name} />
              <AccessRowNameColumn>
                <AccessRowName>{owner.name}</AccessRowName>
                <AccessRowEmail>Document owner</AccessRowEmail>
              </AccessRowNameColumn>
            </AccessRowInfo>
            <AccessRowActions>
              <OwnerLabel>Owner</OwnerLabel>
              {isOwnerOrAdmin && (
                <Tooltip title="Transfer ownership">
                  <IconButton size="small" onClick={onTransferOwnership}>
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </AccessRowActions>
          </AccessRow>
        )}

        {permissions.map((permission) => {
          const isGroup = permission.kind === DocumentPermissionKind.Group;
          return (
            <AccessRow
              key={permission.uuid}
              data-testid={isGroup ? 'document-permission-row-group' : 'document-permission-row-user'}
              aria-label={
                isGroup ? `${permission.group.name}, ${memberLabel(permission.group.memberCount)}` : undefined
              }
            >
              <AccessRowInfo>
                {isGroup ? (
                  <GroupRowIcon aria-hidden>
                    <GroupsOutlinedIcon fontSize="small" />
                  </GroupRowIcon>
                ) : (
                  <Avatar name={permission.user.name} />
                )}
                <AccessRowNameColumn>
                  {isGroup ? (
                    <>
                      <AccessRowName>{permission.group.name}</AccessRowName>
                      <AccessRowEmail>{memberLabel(permission.group.memberCount)}</AccessRowEmail>
                    </>
                  ) : (
                    <AccessRowName>{permission.user.name}</AccessRowName>
                  )}
                </AccessRowNameColumn>
              </AccessRowInfo>
              <AccessRowActions>
                {isOwnerOrAdmin ? (
                  <>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={permission.accessLevel}
                        onChange={(e) => onUpdate(permission.uuid, e.target.value as DocumentAccessLevel)}
                      >
                        <MenuItem value={DocumentAccessLevel.View}>Can view</MenuItem>
                        <MenuItem value={DocumentAccessLevel.Edit}>Can edit</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title="Remove access">
                      <IconButton size="small" onClick={() => onRemove(permission.uuid)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <OwnerLabel>
                    {permission.accessLevel === DocumentAccessLevel.Edit ? 'Can edit' : 'Can view'}
                  </OwnerLabel>
                )}
              </AccessRowActions>
            </AccessRow>
          );
        })}

        {permissions.length === 0 && <EmptyPeopleMessage>No individual access granted yet.</EmptyPeopleMessage>}
      </StyledList>
    </ShareDialogSection>
  )
);

export default PeopleWithAccessList;
